import type { TFunction } from "i18next";
import type { LicenseModel } from "@/lib/generated/model/licenseModel";

export type ValidityError =
  | "valid_from_required"
  | "time_limited"
  | "perpetual"
  | "past_expiry"
  | "validity_window";

/** Returns the validity error shared by the create and edit forms. */
export function getValidityError({
  model,
  validFrom,
  validUntil,
  requireValidFrom = false,
  rejectPastExpiry = false,
  now = new Date(),
}: {
  model: LicenseModel;
  validFrom: string;
  validUntil: string;
  requireValidFrom?: boolean;
  rejectPastExpiry?: boolean;
  now?: Date;
}): ValidityError | null {
  if (requireValidFrom && !validFrom) return "valid_from_required";
  if (model === "time_limited" && !validUntil) return "time_limited";
  if (model === "perpetual" && validUntil) return "perpetual";
  if (
    validFrom &&
    validUntil &&
    new Date(validUntil).getTime() <= new Date(validFrom).getTime()
  ) {
    return "validity_window";
  }
  if (rejectPastExpiry && validUntil && new Date(validUntil).getTime() <= now.getTime()) {
    return "past_expiry";
  }
  return null;
}

export function validityErrorMessage(t: TFunction, error: ValidityError): string {
  if (error === "valid_from_required") {
    return t("licenses.validFromRequiredError", "Activation availability is required.");
  }
  if (error === "validity_window") {
    return t("licenses.validityWindowError", "Expiry must be after activation availability.");
  }
  if (error === "past_expiry") {
    return t("licenses.pastExpiryError", "Expiry must be in the future.");
  }
  return error === "time_limited"
    ? t("licenses.timeLimitedValidityError", "Time-limited licenses require a valid-until timestamp.")
    : t("licenses.perpetualValidityError", "Perpetual licenses cannot have a valid-until timestamp.");
}
